import React, { useState, useEffect } from 'react';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError, confirmDelete } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit, FaTrash } from 'react-icons/fa'; 

const BusinessFormation = () => {
  const [businessFormations, setBusinessFormations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formValues, setFormValues] = useState({ name: '' });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT || 10); 

  // Debounce search query - only trigger when 3+ characters or empty
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 3 || searchQuery.length === 0) {
        setDebouncedSearchQuery(searchQuery);
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchBusinessFormations(currentPage, debouncedSearchQuery);
  }, [currentPage, debouncedSearchQuery]);

  const fetchBusinessFormations = async (page = 1, search = '') => {
    try {
      const res = await axios.get(`/admin/api/businessFormation?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`);
      const { businessFormations = [], totalCount = 0 } = res.data;
      setBusinessFormations(businessFormations);
      setTotalPages(Math.ceil(totalCount / limit));
    } catch (err) {
      console.error('Failed to fetch Formation:', err);
    }
  };

  const handleSave = async () => {
    const validationErrors = validateForm(formValues, {
      name: { required: true },
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      if (editingId) {
        await axios.put(`/admin/api/businessFormation/${editingId}`, formValues);
        toastSuccess('Formation updated successfully!');
      } else {
        await axios.post('/admin/api/businessFormation', formValues);
        toastSuccess('Formation added successfully!');
      }
      setCurrentPage(1);  // reset to first page after save
      fetchBusinessFormations(1, searchQuery);
      handleCancel();
    } catch (err) {
      console.error('Failed to save Formation:', err);
      toastError('Failed to save Formation');
    }
  };

  const handleEdit = (businessFormations) => {
    setFormValues({ name: businessFormations.name });
    setEditingId(businessFormations._id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDelete('Are you sure you want to delete this formation?');
    if (confirmed) {
      try {
        await axios.delete(`/admin/api/businessFormation/${id}`);
        toastSuccess('Formation deleted successfully!');
        setCurrentPage(1);
        fetchBusinessFormations(1, searchQuery);
      } catch (err) {
        console.error('Failed to delete Formation:', err);
        toastError('Failed to delete Formation');
      }
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormValues({ name: '' });
    setEditingId(null);
    setErrors({});
  };

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Formation</h3>
        <input
          type="text"
          className="form-control w-25"
          placeholder="Search Formation (min 3 chars)..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
        />
      </div>
    
      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0">Formation List</h5>
          <button
            className="btn btn-sm btn-success"
            onClick={() => setShowModal(true)}
          >
            + Add New Formation
          </button>
        </div>
       <div className="table-responsive">
        <div className="card-body p-0">
          <table
            className="table table-bordered table-sm mb-0"
          >
            <thead className="table-light">
              <tr>
                <th className="py-1 px-2 w-5">Sr no.</th>
                <th className="py-1 px-2 w-75">Formation Name</th>
                <th className="py-1 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {businessFormations.length > 0 ? (
                businessFormations.map((businessFormations, idx) => (
                  <tr key={businessFormations._id}>
                    <td className="py-1 px-2">{(currentPage - 1) * limit + idx + 1}</td>
                    <td className="py-1 px-2">{businessFormations.name}</td>
                    <td className="py-1 px-2">
                      <button
                        className="btn btn-sm me-1 py-0 px-2 fs-6 lh-sm shadow-none border-0 text-primary-custom"
                        title="Edit"
                        onClick={() => handleEdit(businessFormations)}
                      >
                        <FaEdit size={14} />
                      </button>
                      <button
                        className="btn btn-sm py-0 px-2 fs-6 lh-sm shadow-none border-0 text-danger"
                        onClick={() => handleDelete(businessFormations._id)}
                        title="Delete"
                      >
                        <FaTrash size={14} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="text-center py-2">No Formation found.</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-end align-items-center gap-2 p-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                &laquo;
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className="{`btn btn-sm page-btn ${currentPage === totalPages ? 'active' : ''}`}"
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                &raquo;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
      <CommonModalForm
        visible={showModal}
        title={editingId ? 'Edit Formation' : 'Add Formation'}
        fields={[
              { name: 'name', label: 'Formation', placeholder: 'Enter Formation Name' }
            ]}
        values={formValues}
        errors={errors}
        onChange={setFormValues}
        onSubmit={handleSave}
        onCancel={handleCancel}
        submitLabel={editingId ? 'Update' : 'Save'}
        requiredFields={['name']}
      />
    </div>
  );
};

export default BusinessFormation;
